import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import Switch from 'react-switch'

import {useViewImageContext} from '../../Images/ViewImageContext'
import {useViewLandContext} from '../../ViewLandContext'
import {formatFixedNumber, formatMeters, formatPercent, getNumericValue} from '../../../../../../../shared/numberFormat'
import './CommonMapLegend.style.scss'
import {useMapLegendContext} from './MapLegendContext'
import {getBaseImageType, isHdImageType, STANDARD_IMAGE_TYPES} from '../../Images/imageTypeUtils'
import {parseIndexRangeHeader} from '../../Images/Services/imageLayerPayload'

const measuredNumber = (value) => {
  const number = getNumericValue(value)
  return number != null && number >= 0 ? number : null
}
const measuredPercent = (value) => measuredNumber(value) == null ? '-' : formatPercent(value)

const preventMiddleClick = (event) => {
  if (event.button === 1) {
    event.preventDefault()
    event.stopPropagation()
  }
}

const CommonMapLegend = ({
  className = '',
  headers,
  enhancementScope = 'layers',
  layerLabel = null,
  roleLabel = null,
  showEnhance = true,
}) => {
  const {t} = useTranslation()
  const mapLegendContext = useMapLegendContext()
  const viewImageContext = useViewImageContext()
  const viewLandContext = useViewLandContext()
  const classes = ['int1', 'int2', 'int3', 'int4']
  const isAltimetryLegend = headers?.type === 'altimetry'
  const horizontalResolution = measuredNumber(headers?.horizontalResolutionMeters)
  const verticalResolution = measuredNumber(headers?.verticalResolutionMeters)
  const hasHorizontalResolution = isAltimetryLegend && horizontalResolution > 0
  const hasVerticalResolution = isAltimetryLegend && verticalResolution > 0
  const isSoilMapLegend = headers?.type === 'soil_map'
  const isYieldMapLegend = headers?.type === 'yield_map'
  const intervals = useMemo(() => {
    if (isAltimetryLegend || isSoilMapLegend || isYieldMapLegend) {
      return []
    }

    return [...(parseIndexRangeHeader(headers) || [])].reverse()
  }, [headers, isAltimetryLegend, isSoilMapLegend, isYieldMapLegend])
  const total = useMemo(() => {
    if (isAltimetryLegend || isSoilMapLegend || isYieldMapLegend) {
      return {ha: 0, percentage: 0}
    }

    const sumKnown = (key) => intervals.length > 0 && intervals.every((item) => measuredNumber(item[key]) != null)
      ? intervals.reduce((sum, item) => sum + measuredNumber(item[key]), 0) : null
    return {ha: sumKnown('ha'), percentage: sumKnown('percentage')}
  }, [intervals, isAltimetryLegend, isSoilMapLegend, isYieldMapLegend])
  const landId = viewLandContext.land?.land_id
  const currentLandImage = landId != null ? viewImageContext.getLand?.(landId) : null
  const activeBaseImageType = getBaseImageType(currentLandImage?.imageType)
  const isHdLegend = headers?.['x-product'] === 'planetscope_hd' || (!layerLabel && isHdImageType(currentLandImage?.imageType))
  const indexType = isHdLegend ? headers?.['x-layer'] || activeBaseImageType : activeBaseImageType
  const inferredLayerLabel =
    ['ndvi', 'evi', 'ndre'].includes(indexType)
      ? `${indexType.toUpperCase()}${isHdLegend ? ' HD' : ''}`
      : null
  const normalizedLayerLabel = String(layerLabel || inferredLayerLabel || '').toLowerCase()
  const isEnhanceableLayer =
    activeBaseImageType === STANDARD_IMAGE_TYPES.EVI ||
    activeBaseImageType === STANDARD_IMAGE_TYPES.NDVI ||
    normalizedLayerLabel.includes('evi') ||
    normalizedLayerLabel.includes('ndvi') ||
    (isHdLegend && normalizedLayerLabel.includes('ndre'))
  const resolvedLayerLabel = layerLabel || inferredLayerLabel
  const shouldShowEnhance = showEnhance && isEnhanceableLayer
  const aoiHa = isHdLegend ? measuredNumber(headers?.['x-total-ha']) : null
  const excludedHa = isHdLegend ? measuredNumber(headers?.['x-excluded-ha']) : null
  const hasExcludedArea = aoiHa > 0 && excludedHa != null && excludedHa <= aoiHa
  const displayTotal = hasExcludedArea ? {ha: aoiHa, percentage: 100} : total
  const isComparisonEnhancement = enhancementScope === 'comparison'
  const isEnhanced = landId != null
    ? Boolean(
        isComparisonEnhancement
          ? mapLegendContext.comparisonEnhanced?.[landId]
          : mapLegendContext.enhanced[landId]
      )
    : false
  const handleEnhancementChange = () => {
    if (landId == null) {
      return
    }

    if (isComparisonEnhancement) {
      mapLegendContext.handleComparisonEnhanced(landId)
      return
    }

    mapLegendContext.handleEnhanced(landId)
  }
  let specialLegendClass = 'gis-map-legend--common'

  if (isSoilMapLegend) {
    specialLegendClass = 'gis-map-legend--soil'
  } else if (isAltimetryLegend || isYieldMapLegend) {
    specialLegendClass = 'gis-map-legend--altimetry'
  }
  const rootClassName = [
    'card shadow-sm mb-5 displayable right-card-data gis-map-legend',
    specialLegendClass,
    roleLabel ? 'gis-map-legend--comparison-card' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  if (isAltimetryLegend) {
    return (
      <div className={rootClassName}>
        <div className='card-header'>
          <div className='gis-map-legend__title-group'>
            <h3 className='card-title'>{roleLabel || t('images:legend.title')}</h3>
            {layerLabel && <span className='gis-map-legend__meta'>{layerLabel}</span>}
          </div>
        </div>
        <div className='card-body'>
          <div className='gis-map-legend__altimetry-layout'>
            <div className='gis-map-legend__altimetry-scale' aria-hidden='true' />
            <div className='gis-map-legend__altimetry-values'>
              <div className='gis-map-legend__altimetry-row'>
                <span>{t('images:legend.highest')}</span>
                <strong>{formatMeters(headers.maxHeight)}</strong>
              </div>
              <div className='gis-map-legend__altimetry-row'>
                <span>{t('images:legend.average')}</span>
                <strong>{formatMeters(headers.averageHeight)}</strong>
              </div>
              <div className='gis-map-legend__altimetry-row'>
                <span>{t('images:legend.lowest')}</span>
                <strong>{formatMeters(headers.minHeight)}</strong>
              </div>
            </div>
          </div>
          {(hasHorizontalResolution || hasVerticalResolution) && (
            <dl className='gis-map-legend__altimetry-resolution'>
              {hasHorizontalResolution && (
                <div>
                  <dt>{t('images:legend.resolution_xy')}</dt>
                  <dd>{formatMeters(horizontalResolution)}</dd>
                </div>
              )}
              {hasVerticalResolution && (
                <div>
                  <dt>{t('images:legend.resolution_z')}</dt>
                  <dd>{formatMeters(verticalResolution)}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </div>
    )
  }

  if (isYieldMapLegend) {
    return (
      <div className={rootClassName}>
        <div className='card-header'>
          <div className='gis-map-legend__title-group'>
            <h3 className='card-title'>{roleLabel || t('images:legend.title')}</h3>
            <span className='gis-map-legend__meta'>{layerLabel || t('images:selector.yield_map')}</span>
          </div>
        </div>
        <div className='card-body'>
          <div className='gis-map-legend__altimetry-layout'>
            <div className='gis-map-legend__yield-scale' aria-hidden='true' />
            <div className='gis-map-legend__altimetry-values'>
              <div className='gis-map-legend__altimetry-row'>
                <span>{t('images:legend.highest_yield')}</span>
                <strong>{formatFixedNumber(headers.maxYieldTonsHa)} t/ha</strong>
              </div>
              <div className='gis-map-legend__altimetry-row'>
                <span>{t('images:legend.average')}</span>
                <strong>{formatFixedNumber(headers.averageYieldTonsHa)} t/ha</strong>
              </div>
              <div className='gis-map-legend__altimetry-row'>
                <span>{t('images:legend.lowest_yield')}</span>
                <strong>{formatFixedNumber(headers.minYieldTonsHa)} t/ha</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isSoilMapLegend) {
    const scales = (headers.scales || [])
      .map((scale) => scale?.label)
      .filter(Boolean)
      .join(' / ')
    const sourceName = headers.source?.name
    const sourceUrl = headers.source?.url
    const memory = headers.documentation?.memory

    return (
      <div className={rootClassName}>
        <div className='card-header'>
          <div className='gis-map-legend__title-group'>
            <h3 className='card-title'>{roleLabel || t('images:legend.title')}</h3>
            <span className='gis-map-legend__meta'>
              {layerLabel || t('images:selector.soil_map')}
            </span>
          </div>
        </div>
        <div className='card-body'>
          <div className='gis-map-legend__soil-items'>
            {(headers.items || []).map((item, index) => (
              <div
                className='gis-map-legend__soil-item'
                key={`${item.label || 'soil-unit'}-${index}`}
              >
                <span
                  className='gis-map-legend__soil-swatch'
                  style={{backgroundColor: item.color || '#94a3b8'}}
                  aria-hidden='true'
                />
                <span className='gis-map-legend__soil-copy'>
                  <span>{item.label || t('images:soil_map.unknown_unit')}</span>
                  {item.metadataLabel && <small>{item.metadataLabel}</small>}
                </span>
              </div>
            ))}
          </div>
          <dl className='gis-map-legend__soil-metadata'>
            {headers.dataset && (
              <div>
                <dt>{t('images:soil_map.dataset')}</dt>
                <dd>{headers.dataset}</dd>
              </div>
            )}
            {scales && (
              <div>
                <dt>{t('images:soil_map.scale')}</dt>
                <dd>{scales}</dd>
              </div>
            )}
            {headers.coverage?.percentage != null && (
              <div>
                <dt>{t('images:soil_map.coverage')}</dt>
                <dd>{formatPercent(headers.coverage.percentage)}</dd>
              </div>
            )}
            {memory?.title && (
              <div>
                <dt>{t('images:soil_map.memory')}</dt>
                <dd>
                  {memory.url ? (
                    <a
                      href={memory.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      onMouseDown={preventMiddleClick}
                      onAuxClick={preventMiddleClick}
                    >
                      {memory.title}
                    </a>
                  ) : memory.title}
                </dd>
              </div>
            )}
            {sourceName && (
              <div>
                <dt>{t('images:soil_map.source')}</dt>
                <dd>
                  {sourceUrl ? (
                    <a href={sourceUrl} target='_blank' rel='noreferrer'>
                      {sourceName}
                    </a>
                  ) : sourceName}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    )
  }

  if (intervals.length === 0) return null

  return (
    <div className={rootClassName}>
      <div className='card-header'>
        <div className='gis-map-legend__title-group'>
          <h3 className='card-title'>{roleLabel || t('images:legend.title')}</h3>
          {(resolvedLayerLabel || shouldShowEnhance) && (
            <div className='gis-map-legend__meta-row'>
              {resolvedLayerLabel && <span className='gis-map-legend__meta'>{resolvedLayerLabel}</span>}
              {shouldShowEnhance && (
                <div className='gis-map-legend__toolbar'>
                  <span className='gis-map-legend__toolbar-label'>{t('images:legend.enhance')}</span>
                  <Switch
                    disabled={viewImageContext.loading || landId == null}
                    onChange={handleEnhancementChange}
                    checked={isEnhanced}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className='card-body'>
        <div className='gis-map-legend__table'>
          <div className='gis-map-legend__table-row gis-map-legend__table-row--head'>
            <span className='gis-map-legend__cell gis-map-legend__cell--swatch' />
            <span className='gis-map-legend__cell gis-map-legend__cell--label'>
              {t('images:legend.index')}
            </span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>ha</span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>%</span>
          </div>
          {intervals.map((interval, index) => (
            <div className='gis-map-legend__table-row' key={`legend-${index}-${interval.cluster}`}>
              <span className='gis-map-legend__cell gis-map-legend__cell--swatch' aria-hidden='true'>
                <span className={`box-square ${isHdLegend ? '' : classes[index] || ''}`.trim()}
                  style={/^#[0-9a-f]{6}$/i.test(interval.color || '') ? {backgroundColor: interval.color} : undefined} />
              </span>
              <span className='gis-map-legend__cell gis-map-legend__cell--label'>
                {interval.cluster}
              </span>
              <span className='gis-map-legend__cell gis-map-legend__cell--value'>
                {formatFixedNumber(measuredNumber(interval.ha))}
              </span>
              <span className='gis-map-legend__cell gis-map-legend__cell--value'>
                {measuredPercent(interval.percentage)}
              </span>
            </div>
          ))}
          {hasExcludedArea && <div className='gis-map-legend__table-row'>
            <span className='gis-map-legend__cell gis-map-legend__cell--swatch' />
            <span className='gis-map-legend__cell gis-map-legend__cell--label'>
              {t('images:legend.excluded_area', {defaultValue: 'Sin datos válidos'})}
            </span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>{formatFixedNumber(excludedHa)}</span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>{formatPercent(excludedHa * 100 / aoiHa)}</span>
          </div>}
          <div className='gis-map-legend__table-row gis-map-legend__table-row--total'>
            <span className='gis-map-legend__cell gis-map-legend__cell--swatch' />
            <span className='gis-map-legend__cell gis-map-legend__cell--label'>
              {t('images:legend.total')}
            </span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>
              {formatFixedNumber(displayTotal.ha)}
            </span>
            <span className='gis-map-legend__cell gis-map-legend__cell--value'>
              {measuredPercent(displayTotal.percentage)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CommonMapLegend
